import { createElement, memo, forwardRef, Fragment, ReactElement, useState } from 'react';
import {
  FontStyle,
  TextColor,
  renderInlineCssVars,
  useDesignTokens,
  DesignTokens,
} from '../../../theme';
import { SliderFormat } from '../SliderFormat';
import { SliderScale } from '../SliderScale';
import { Label } from '../../Label';
import { useRefArray } from '../../../hooks/useRefArray';
import { useResizeObserverRef } from '../../../hooks/useResizeObserver';
import { postfixTestId, TestIdProps } from '../../../util/testId';
import { setRef } from '../../../util/ref-util';
import cx from 'classnames';
import cn from './SliderTickLabels.css.json';
import './SliderTickLabels.css';
export interface SliderTickLabels extends TestIdProps {
  ticks: number[] | undefined;
  tickIds: string[];
  tickLabels?: (string | undefined)[];
  orientation: 'horizontal' | 'vertical';
  format: SliderFormat;
  scale: SliderScale;

  disabled?: boolean;

  labelTokens:
    | undefined
    | null
    | {
        theme?: () => Partial<TextColor> | undefined;
        font?: Partial<FontStyle>;
      };
}

export const SliderTickLabels = memo(
  forwardRef((props: SliderTickLabels, ref) => {
    const dt = useDesignTokens();
    const [firstLabelWidth, setFirstLabelWidth] = useState(0);
    const { refs: labelRefs, createSetRef: createLabelRef } = useRefArray<HTMLElement>();
    const [setLabelsWrapperRef, getLabelsWrapperRef] = useResizeObserverRef<HTMLDivElement>(() => {
      const secondLabel = labelRefs[1];
      const wrapper = getLabelsWrapperRef();

      if (labelRefs.length === 1 && wrapper) {
        setFirstLabelWidth(wrapper.getBoundingClientRect().width / 2);
      }

      if (secondLabel) {
        setFirstLabelWidth(secondLabel.getBoundingClientRect().width / 2);
      }
    });

    let tokenStyle;
    tokenStyle = getSliderTickLabelToken(dt);
    if (props.labelTokens) {
      tokenStyle = { ...tokenStyle, ...props.labelTokens.font };
      if (props.labelTokens.theme) {
        tokenStyle = { ...tokenStyle, ...props.labelTokens.theme() };
      }
    }

    const tokens = renderInlineCssVars(
      {
        ...tokenStyle,
        'spacing-xs': dt.spacing.core.xs,
        'font-size-s': dt.enableNovaTokens ? dt.fontNova.size.s : dt.font.size.s,
      },
      'slider-tick-label'
    );

    const isHorizontal = props.orientation === 'horizontal';

    const labels: ReactElement[] = [];
    if (props.ticks) {
      let total = 0;

      for (let i = 0, l = props.ticks.length; i < l; ++i) {
        const value = props.ticks[i];
        const lastValue = props.ticks[i - 1] as typeof value | undefined;

        let valueText: string | undefined;
        if (props.tickLabels?.length) {
          valueText = props.tickLabels[i];
        } else {
          valueText = props.format.format(value);
        }

        const pos = props.scale.position(value);

        let itemLength = pos;
        if (lastValue !== undefined) {
          itemLength = pos - props.scale.position(lastValue);
        }
        if (i === l - 1 && value === props.scale.maxValue) {
          // NOTE: handles case when scale.minValue === scale.maxValue
          itemLength = 1 - total;
        }

        total += itemLength;

        labels.push(
          <div
            data-testid={postfixTestId(props.testId, `label${i}`)}
            id={props.tickIds[i]}
            key={i}
            ref={(el) => createLabelRef(i)(el)}
            style={{
              [props.orientation === 'vertical' ? 'height' : 'width']: `${itemLength * 100}%`,
            }}
          >
            <span
              style={i === 0 && isHorizontal ? { width: `${firstLabelWidth}px` } : {}}
              className={cx(
                cn.label[props.orientation],
                props.disabled && cn.disabled,
                {
                  [cn.labelHorizontalFirst]:
                    value === props.scale.minValue && i === 0 && isHorizontal,
                },
                {
                  [cn.labelHorizontalLast]:
                    value === props.scale.maxValue && i === l - 1 && isHorizontal,
                }
              )}
            >
              <Label className={cn.sliderTickLabel} wrapText={false}>
                {valueText}
              </Label>
            </span>
          </div>
        );
      }
    }

    return (
      <Fragment>
        <div
          data-testid={props.testId}
          className={cn.labelContainer[props.orientation]}
          style={tokens}
          ref={(el) => {
            setRef(ref, el);
            setRef(setLabelsWrapperRef, el);
          }}
        >
          {props.orientation === 'vertical' ? labels.reverse() : labels}
        </div>
      </Fragment>
    );
  })
);

const getSliderTickLabelToken = (dt: DesignTokens) => {
  if (dt.enableNovaTokens) {
    return {
      fg: dt.c.colorTextLabel,
      'disabled-fg': dt.stateStandardNova.disabled.fg,
    };
  } else {
    switch (dt.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: dt.c.white,
        };
    }
    switch (dt.themeVariant) {
      case 'high-contrast':
        return {
          fg: dt.c.n2,
        };
      case 'dark':
        return {
          fg: dt.c.white,
        };
      default:
        return {
          fg: dt.c.n_1,
        };
    }
  }
};
